import Vue from 'vue';
import { Permission, User, UserGroup, UserRole } from '@/interfaces/user.interface';
import UserService from '@/services/api/user.service';
import { router } from '@/router';
import { RootState } from '@/store/store';
import { Module } from 'vuex';

import PermissionsService from '../services/permissions.service';
import { VuetifyPaginationInterface } from '@/interfaces/vuetify-pagination.interface';

const jwt = require('jsonwebtoken');

export const mutations = {
  SET_USERS(state: any, users: User[]) {
    Vue.set(state, 'users', users);
  },
  setRoles(state: any, roles: UserRole[]) {
    Vue.set(state, 'roles', roles);
  },
  setGroups(state: any, groups: UserGroup[]) {
    Vue.set(state, 'groups', groups);
  },
  setUsersFilter(state: any, filters: any) {
    Vue.set(state, 'userFilters', {...state.userFilters, ...filters});
  },
  setPermissions(state: any, permissions: Permission[]) {
    Vue.set(state, 'permissions', permissions);
  },
  SET_USER(state: any, user: User) {
    Vue.set(state, 'user', user);
  },
  CREATE_USER(state: any, {user}: { user: User }) {
    Vue.set(state, 'users', [...state.users, user]);
  },
  UPDATE_USER(state: any, {user}: { user: User }) {
    Vue.set(
        state,
        'users',
        state.users.map((u: User) => u.uuid === user.uuid ? {...u, ...user} : u)
    );
  },
  DELETE_USER(state: any, {user}: { user: User }) {
    Vue.set(state, 'users', state.users.filter((u: User) => u.uuid !== user.uuid));
  },
  LOG_IN_FAILED(state: any) {
    Vue.set(state, 'user', {});
  },
  LOG_OUT(state: any) {
    Vue.set(state, 'user', {});
  },
  LOG_IN_REQUEST(state: any, user: User) {
    Vue.set(state, 'user', user);
  },
  setUserDialogVisibility(state: any, isVisible: boolean) {
    Vue.set(state, 'addUserDialogVisible', isVisible);
  },
  setPagination(state: any, {pagination}: { pagination: VuetifyPaginationInterface }) {
    Vue.set(state, 'pagination', pagination);
  },
};

export const actions = {
  async fetchUsers({commit}: any) {
    commit('LOADING_START', null, {root: true});
    try {
      const users: User[] = await UserService.getAll();
      commit('LOADING_FINISH', null, {root: true});
      commit('SET_USERS', users);
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async fetchRoles({commit}: any) {
    commit('LOADING_START', null, {root: true});
    try {
      const roles: UserRole[] = await UserService.fetchRoles();
      commit('LOADING_FINISH', null, {root: true});
      commit('setRoles', roles);
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async fetchGroups({commit}: any) {
    commit('LOADING_START', null, {root: true});
    try {
      const groups: UserGroup[] = await UserService.fetchGroups();
      commit('LOADING_FINISH', null, {root: true});
      commit('setGroups', groups);
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  async fetchPermissions({commit}: any) {
    commit('LOADING_START', null, {root: true});
    try {
      const permissions: Permission[] = await UserService.fetchPermissions();
      commit('LOADING_FINISH', null, {root: true});
      commit('setPermissions', permissions);
      PermissionsService.setPermissions(permissions);
    } catch (e) {
      commit('LOADING_FINISH', null, {root: true});
      console.log('error', e);
    }
  },
  SET_AUTH_TOKEN({commit}: any, token: string) {
    commit('SET_AUTH_TOKEN', token);
  },
  UNSET_AUTH_TOKEN({commit}: any) {
    commit('SET_AUTH_TOKEN', null);
  },
  SET_USER({commit}: any, user: User) {
    commit('SET_USER', user);
  },
  UNSET_USER({commit}: any) {
    commit('SET_USER', {});
  },
  async createUser(
      {commit, dispatch}: any,
      {user, successAction, successPayload}: { user: User, successAction: string, successPayload: any }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await UserService.create(user);
      commit('CREATE_USER', {user: response});
      if (successAction) dispatch(successAction, successPayload);
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Crating user failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async UPDATE_USER(
      {commit, dispatch}: any,
      {user, successAction, successPayload}: { user: User, successAction: string, successPayload: any }) {
    commit('LOADING_START', null, {root: true});
    try {
      const response = await UserService.update(user);
      commit('UPDATE_USER', {user: response});
      if (successAction) dispatch(successAction, successPayload);
      dispatch('SET_ALERT_MESSAGE', 'User updated', {root: true});
    } catch (e) {
      dispatch('SET_ALERT_MESSAGE', 'Updating user failed', {root: true});
      console.log(e);
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async deleteWithConfirmation({commit}: any, {user}: { user: User }) {
    commit('SHOW_CONFIRMATION', {
      message: `Delete ${user.email}?`,
      action: 'users/deleteUser',
      payload: {user}
    }, {root: true});
  },
  async deleteUser({commit, dispatch}: any, {user}: { user: User }) {
    commit('LOADING_START', null, {root: true});
    try {
      if (!user.uuid) return dispatch('SET_ALERT_MESSAGE', `Can't delete a user without a uuid`, {root: true});
      await UserService.delete(user.uuid);
      await commit('DELETE_USER', {user});
    } catch (e) {
      console.log(e);
      dispatch('SET_ALERT_MESSAGE', 'Deleting user failed', {root: true});
    } finally {
      commit('LOADING_FINISH', null, {root: true});
    }
  },
  async LOG_IN({dispatch, commit}: any, {email, password}: { email: string, password: string }) {
    commit('LOG_IN_REQUEST', {email});
    try {
      const response = await UserService.login(email, password);
      commit('SET_USER', response.user);
      localStorage.setItem('user', JSON.stringify(response.user));
      localStorage.setItem('authToken', response.token);
      router.push('/');
    } catch (error) {
      commit('LOG_IN_FAILED');
      try {
        dispatch('SET_ALERT_MESSAGE', error.response.data.message, {root: true});
      } catch (e) {
        dispatch('SET_ALERT_MESSAGE', 'Login failed', {root: true});
      }
    }
  },
  async LOG_OUT({commit}: any) {
    UserService.logout();
    commit('LOG_OUT');
  },
  setUsersFilter({commit}: any, filters: any) {
    commit('setUsersFilter', filters);
  },
  closeAddUserDialog({commit}: any) {
    commit('setUserDialogVisibility', false);
  },
  openAddUserDialog({commit}: any) {
    commit('setUserDialogVisibility', true);
  },
  setPagination({commit}: any, {pagination}: {pagination: VuetifyPaginationInterface}) {
      commit('setPagination', {pagination});
  }
};

export const getters = {
  currentUser(state: any): any {
    try {
      const token = localStorage.getItem('authToken');
      const jwtKey = process.env.VUE_APP_JWT_PUBLIC_KEY;
      const userFromToken: User = jwt.verify(token, jwtKey).user;
      if (userFromToken) {
        return state.users.find((u: User) => u.uuid === userFromToken.uuid);
      }
    } catch (e) {
      UserService.logout();
      console.log('cant get user: ', e);
    }
  }
};

export interface UsersState {
  users: User[],
  user: User,
  userFilters: {
    search: string
  },
  addUserDialogVisible: boolean,
  editUserDialogVisible: boolean,
  pagination: VuetifyPaginationInterface,
}

export const usersState: UsersState = {
  users: [] as User[],
  user: {} as User,
  userFilters: {
    search: ''
  },
  addUserDialogVisible: false,
  editUserDialogVisible: false,
  pagination: {
      page: 1,
      itemsPerPage: 5,
      sortBy: [''],
      sortDesc: [false]
  },
};

export const usersStoreModule: Module<UsersState, RootState> = {
  namespaced: true,
  state: usersState,
  actions,
  mutations,
  getters
};
